import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Receipt, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link, useParams } from 'react-router-dom';

import { apiGet, apiPost } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pill, StatusPill } from '@/components/ui/Pill';
import { StatCard } from '@/components/ui/StatCard';
import { ErrorState, LoadingState } from '@/components/ui/States';
import type { BillRow, CustomerRow } from '@/types/api';
import { fmtDate, fmtPhone } from '@/utils/format';

/**
 * Customer 360 — header card, KPIs, recent bills, tags, two actions
 * (Send WhatsApp reminder, Collect bulk payment). The shape we read
 * is whatever `/customers/{id}` returns; we consume defensively so
 * upstream tweaks don't break the page.
 */
export function Customer360Page() {
  const { id } = useParams();
  const customerId = Number(id);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () =>
      apiGet<CustomerRow & { bills?: BillRow[] }>(endpoints.customer(customerId)),
    enabled: Number.isFinite(customerId),
  });

  const reminderMut = useMutation({
    mutationFn: () => apiPost(endpoints.customerReminder(customerId)),
    onSuccess: () => toast.success('Reminder sent'),
    onError: (e: Error) => toast.error(e.message),
  });

  const collectMut = useMutation({
    mutationFn: (mode: 'cash' | 'upi') =>
      apiPost(endpoints.customerCollectBulk(customerId), {
        amount: query.data?.due_amount ?? 0,
        mode,
      }),
    onSuccess: () => {
      toast.success('Payment recorded');
      queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!Number.isFinite(customerId)) return <ErrorState title="Invalid customer id" />;
  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        title="Couldn't load customer"
        description={(query.error as Error)?.message}
        onRetry={() => query.refetch()}
      />
    );
  }

  const c = query.data;
  const bills = c.bills ?? [];
  const due = c.due_amount ?? 0;

  return (
    <>
      <PageHeader
        back
        title={c.name}
        description={fmtPhone(c.phone)}
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="success"
              iconLeft={<MessageCircle className="h-4 w-4" />}
              loading={reminderMut.isPending}
              onClick={() => reminderMut.mutate()}
            >
              Send WhatsApp reminder
            </Button>
            {due > 0 && (
              <>
                <Button
                  iconLeft={<Wallet className="h-4 w-4" />}
                  loading={collectMut.isPending && collectMut.variables === 'cash'}
                  onClick={() => collectMut.mutate('cash')}
                >
                  Collect cash
                </Button>
                <Button
                  variant="outline"
                  iconLeft={<Wallet className="h-4 w-4" />}
                  loading={collectMut.isPending && collectMut.variables === 'upi'}
                  onClick={() => collectMut.mutate('upi')}
                >
                  Collect via UPI
                </Button>
              </>
            )}
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6">
        <StatCard label="Total spent" value={<Money value={c.total_spent ?? 0} />} />
        <StatCard label="Bills" value={c.bills_count ?? 0} />
        <StatCard
          label="Outstanding due"
          value={<Money value={due} />}
          delta={due > 0 ? 'Reminder pending' : 'All clear'}
          deltaTone={due > 0 ? 'warn' : 'success'}
        />
      </div>

      {/* Tags + credit limit */}
      <Card className="mb-6">
        <SectionHeader title="Profile" />
        <dl className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <dt className="text-ink-soft text-xs uppercase">Email</dt>
            <dd className="font-medium">{c.email || '—'}</dd>
          </div>
          <div>
            <dt className="text-ink-soft text-xs uppercase">Credit limit</dt>
            <dd className="font-medium">
              {c.credit_limit && c.credit_limit > 0 ? (
                <Money value={c.credit_limit} />
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt className="text-ink-soft text-xs uppercase">Tags</dt>
            <dd className="flex flex-wrap gap-1 mt-1">
              {(c.tags ?? []).length === 0 ? (
                <span className="text-ink-soft">—</span>
              ) : (
                (c.tags ?? []).map((t) => (
                  <Pill key={t} tone="brand">
                    {t}
                  </Pill>
                ))
              )}
            </dd>
          </div>
        </dl>
      </Card>

      {/* Recent bills */}
      <Card padded={false}>
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <h2 className="text-base font-semibold">Bills ({bills.length})</h2>
        </div>
        {bills.length === 0 ? (
          <div className="px-5 py-12 text-center text-ink-soft">
            No bills for this customer yet.
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {bills.slice(0, 25).map((b) => (
              <li key={b.id}>
                <Link
                  to={`/bills/${b.id}`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-surface-2"
                >
                  <div className="h-9 w-9 rounded-lg bg-brand-500/10 text-brand-700 grid place-items-center">
                    <Receipt className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono">{b.bill_number}</div>
                    <div className="text-xs text-ink-soft">{fmtDate(b.created_at)}</div>
                  </div>
                  <StatusPill status={b.status} />
                  <Money value={b.total} className="font-semibold" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
